import { useState } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { CONDICIONES_IVA, ETIQUETAS_CONDICION_IVA, errorIdentificacionCliente, etiquetaCondicionIva } from '../lib/condicionIva';

interface Cliente {
  id: number;
  nombre: string;
  razonSocial?: string;
  cuit?: string;
  /** Condición frente al IVA (código de la lista cerrada); null = sin cargar */
  condicionIva?: string | null;
  email?: string;
  telefono?: string;
  direccion?: string;
  condicionPagoId?: number | null;
  condicionPago?: { id: number; nombre: string } | null;
  activo: boolean;
}

interface CondicionPagoOpcion { id: number; nombre: string; tipo?: string }

const emptyForm = () => ({ nombre: '', razonSocial: '', cuit: '', condicionIva: '', email: '', telefono: '', direccion: '', condicionPagoId: '' });

export function ClientesPage() {
  const { data, loading, refetch } = useApi<Cliente[]>('/api/v3/clientes');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = (data ?? []).filter(row =>
    Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())),
  );
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [nombreError, setNombreError] = useState('');
  const toast = useToast();
  const { can } = useAuth();
  const { data: cpResponse } = useApi<any>('/api/v3/condiciones-pago?activo=true');
  const condicionesPago: CondicionPagoOpcion[] = (cpResponse as any)?.data ?? [];
  // La condición actual del cliente puede estar inactiva: se muestra igual para no perderla al editar.
  const condicionActual = editingId ? (data ?? []).find(c => c.id === editingId)?.condicionPago : null;
  const opcionesCondicion = condicionActual && !condicionesPago.some(cp => cp.id === condicionActual.id)
    ? [...condicionesPago, { id: condicionActual.id, nombre: `${condicionActual.nombre} (inactiva)` }]
    : condicionesPago;

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setNombreError('El nombre es obligatorio');
      toast.addToast('El nombre es obligatorio', 'error');
      return;
    }
    setNombreError('');
    const errorCuit = errorIdentificacionCliente(form.cuit, form.condicionIva);
    if (errorCuit) {
      toast.addToast(errorCuit, 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nombre: form.nombre,
        razonSocial: form.razonSocial || undefined,
        // Al editar, vacío = sin CUIT (null): permite borrar un CUIT viejo que no pasa la validación.
        cuit: form.cuit || (editingId ? null : undefined),
        // Al editar, vacío = sin condición (null); al crear se omite.
        condicionIva: form.condicionIva || (editingId ? null : undefined),
        email: form.email || undefined,
        telefono: form.telefono || undefined,
        direccion: form.direccion || undefined,
        // Al editar, vacío = sin condición (null); al crear se omite.
        condicionPagoId: form.condicionPagoId ? Number(form.condicionPagoId) : editingId ? null : undefined,
      };
      if (editingId) {
        await api.patch(`/api/v3/clientes/${editingId}`, payload);
        toast.addToast('Cliente actualizado', 'success');
      } else {
        await api.post('/api/v3/clientes', payload);
        toast.addToast('Cliente creado', 'success');
      }
      cerrarForm();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (c: Cliente) => {
    if (!can('clientes', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setForm({
      nombre: c.nombre, razonSocial: c.razonSocial ?? '', cuit: c.cuit ?? '', condicionIva: c.condicionIva ?? '',
      email: c.email ?? '', telefono: c.telefono ?? '', direccion: c.direccion ?? '',
      condicionPagoId: c.condicionPagoId != null ? String(c.condicionPagoId) : '',
    });
    setEditingId(c.id);
    setNombreError('');
    setOpen(true);
  };

  const cerrarForm = () => { setOpen(false); setEditingId(null); setForm(emptyForm()); setNombreError(''); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);

  const columns = [
    { key: 'nombre', header: 'Nombre' },
    { key: 'cuit', header: 'CUIT / DNI' },
    { key: 'condicionIva', header: 'Condición IVA', render: (r: Cliente) => etiquetaCondicionIva(r.condicionIva) ?? '—', filterable: false },
    { key: 'email', header: 'Email' },
    { key: 'telefono', header: 'Teléfono' },
    { key: 'condicionPago', header: 'Condición de pago', render: (r: Cliente) => r.condicionPago?.nombre ?? 'Contado', filterable: false },
    { key: 'activo', header: 'Activo', render: (r: Cliente) => (r.activo ? 'Sí' : 'No'), filterable: false },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Clientes</h1>
          <HelpTip tips={['Alta de clientes del lado venta', 'El nombre es obligatorio; CUIT/email/teléfono son opcionales', 'El CUIT, si se carga, tiene que tener 11 dígitos (XX-XXXXXXXX-X); un consumidor final (o sin condición) puede tener DNI', 'Un cliente se usa en órdenes de venta, facturas y cobros', 'La condición de pago define los vencimientos (y cuotas) de sus facturas de mostrador']} />
          <p className="text-slate-500 text-sm mt-1">Maestro de clientes</p>
        </div>
        <Button
          onClick={() => {
            setEditingId(null);
            setForm(emptyForm());
            setNombreError('');
            setOpen(true);
          }}
          disabled={!can('clientes', 'crear')}
          title={!can('clientes', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          + Nuevo
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={handleEdit}
        filters={filters}
        onFilterChange={(k, v) => setFilters(f => v ? { ...f, [k]: v } : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)))}
      />
      <Modal
        open={open}
        cierre={cierreForm}
        title={editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            {nombreError && <p className="text-red-600 text-sm">{nombreError}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Razón Social</label>
              <input value={form.razonSocial} onChange={e => setForm({ ...form, razonSocial: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CUIT / DNI</label>
              <input value={form.cuit} onChange={e => setForm({ ...form, cuit: e.target.value })} placeholder="XX-XXXXXXXX-X" className="w-full px-3 py-2 border rounded-lg" />
              <p className="text-xs text-slate-500 mt-1">Consumidor final: podés cargar el DNI</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Condición frente al IVA</label>
            <select value={form.condicionIva} onChange={e => setForm({ ...form, condicionIva: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
              <option value="">— Sin cargar —</option>
              {CONDICIONES_IVA.map(c => <option key={c} value={c}>{ETIQUETAS_CONDICION_IVA[c]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Dirección</label>
            <input value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Condición de pago</label>
            <select value={form.condicionPagoId} onChange={e => setForm({ ...form, condicionPagoId: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Sin condición (contado)</option>
              {opcionesCondicion.map(cp => <option key={cp.id} value={cp.id}>{cp.nombre}{cp.tipo ? ` (${cp.tipo})` : ''}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">Se usa para calcular los vencimientos de sus facturas de mostrador. Las ventas de MercadoLibre no la usan: las cobra MercadoPago.</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={cierreForm.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={handleSave} loading={submitting}>
              {submitting ? 'Guardando...' : editingId ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
